import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { casosApi } from '../api/casos'
import api from '../api/client'
import Boton from '../components/Boton'
import { colors, font, text, space, radius, sx } from '../design/tokens'
import { fmtFecha, fmtFechaHora, fmtMonto, hoyISO } from '../utils/fecha'
import { abrirArchivoEnPestana } from '../utils/descargar'

/*
 * Caso de DEVOLUCIÓN por producto defectuoso (Martin, 2 oct 2026 —
 * docs/diseno-devolucion-defectuoso.md). La pantalla solo PINTA los pasos que
 * arma el backend (CasoDevolucionService::pasos) y ofrece la acción del paso
 * pendiente: retiro → recepción → DEVOLUCIÓN en Presea → reintegros.
 */

const ICONO = { hecho: '✓', pendiente: '●', en_curso: '⏳', bloqueado: '○', no_aplica: '–' }
const COLOR_PASO = {
  hecho: { color: colors.ok, bg: colors.okBg },
  pendiente: { color: colors.warn, bg: colors.warnBg },
  en_curso: { color: colors.info, bg: colors.infoBg },
  bloqueado: { color: colors.textMeta, bg: colors.bgSoft },
  no_aplica: { color: colors.textMeta, bg: colors.bgSoft },
}

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))

/** Remito de retiro para el transporte propio: lo firma el cliente al entregar la mercadería. */
function imprimirRemitoRetiro({ caso, entrega, cliente }) {
  const falta = '<em>(sin dato)</em>'
  const dato = (v) => (String(v ?? '').trim() ? esc(v) : falta)
  const filas = (caso.items || []).map(i =>
    `<tr><td class="mono">${esc(i.codigo)}</td><td>${esc(i.descripcion)}</td><td class="num mono">${esc(i.cantidad)}</td></tr>`).join('')
  const w = window.open('', '_blank')
  if (!w) return
  w.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Remito de retiro · Caso ${caso.id}</title>
<style>
body{font-family:-apple-system,"Segoe UI",Roboto,Arial,sans-serif;color:#0f172a;margin:32px;font-size:13px}
h1{font-size:20px;margin:0}.mono{font-family:ui-monospace,Menlo,monospace}
.head{display:flex;justify-content:space-between;border-bottom:2px solid #0f172a;padding-bottom:10px;margin-bottom:16px}
.lbl{font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#64748b;font-weight:600;margin:14px 0 4px}
table{width:100%;border-collapse:collapse;margin-top:6px}th,td{border-bottom:1px solid #e2e8f0;padding:6px 4px;text-align:left}
th{font-size:11px;text-transform:uppercase;color:#64748b}.num{text-align:right}
.firmas{display:flex;gap:32px;margin-top:48px}.firma{flex:1;border-top:1px solid #0f172a;padding-top:6px;font-size:12px;color:#475569}
em{color:#991b1b}
</style></head><body>
<div class="head"><div><h1>REMITO DE RETIRO</h1><div>LIGIER S.A. — devolución de mercadería</div></div>
<div style="text-align:right"><div class="mono" style="font-size:18px">Caso ${esc(caso.id)}</div>
<div>Fecha de retiro: <span class="mono">${caso.retiro_fecha ? esc(fmtFecha(caso.retiro_fecha)) : falta}</span></div>
<div>Transporte: ${caso.retiro_fletero?.nombre ? esc(caso.retiro_fletero.nombre) : falta}</div></div></div>
<div class="lbl">Cliente</div><div><strong>${dato(cliente?.nombre)}</strong></div>
<div>Contacto en el domicilio: ${dato(entrega?.nombre)} · Tel. <span class="mono">${dato(entrega?.telefono)}</span></div>
<div class="lbl">Retirar en (dirección de entrega)</div>
<div>${dato(entrega?.domicilio)}</div>
<div>${dato(entrega?.localidad)}, ${dato(entrega?.provincia)} · CP <span class="mono">${dato(entrega?.codigo_postal)}</span></div>
<div class="lbl">Comprobantes de la venta</div>
<div>Factura <span class="mono">${dato(caso.nota_venta?.numero_presea)}</span> · Remito <span class="mono">${dato(caso.remito?.numero_presea)}</span> · Pedido <span class="mono">${dato(caso.nota_venta?.numero_lgr)}</span></div>
<div class="lbl">Mercadería que se retira</div>
<table><thead><tr><th>Código</th><th>Producto</th><th class="num">Cant.</th></tr></thead><tbody>${filas}</tbody></table>
<div class="lbl">Motivo</div><div>${dato(caso.detalle)}</div>
<div class="firmas"><div class="firma">Entrega (cliente): firma, aclaración y DNI</div><div class="firma">Retira (transporte): firma y aclaración</div></div>
<script>window.onload=function(){window.print()}</script></body></html>`)
  w.document.close()
}

export default function CasoDevolucionPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [msg, setMsg] = useState('')
  const [enviando, setEnviando] = useState('')
  const [fleteros, setFleteros] = useState([])
  const [form, setForm] = useState({})          // valores de los inputs por paso
  const [reparto, setReparto] = useState({})    // pago_id → importe

  const cargar = useCallback(async () => {
    try {
      const { data: d } = await casosApi.obtener(id)
      setData(d)
      setReparto(Object.fromEntries((d.reintegros || []).map(r => [r.pago.id, String(r.importe)])))
      if (d.caso?.medio_retiro === 'propio') {
        api.get('/fleteros').then(({ data: f }) => setFleteros((f.data || f || []).filter(x => x.activo && !x.interno))).catch(() => {})
      }
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo cargar el caso.')
    } finally { setCargando(false) }
  }, [id])

  useEffect(() => { cargar() }, [cargar])

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  const accion = async (clave, fn, ok) => {
    setEnviando(clave); setError(''); setMsg('')
    try {
      const { data: d } = await fn()
      setData(d)
      setMsg(d.mensaje || ok)
      setForm({})
    } catch (e) {
      const errs = e.response?.data?.errors
      setError(errs ? Object.values(errs).flat().join(' ') : (e.response?.data?.message || 'No se pudo completar la acción.'))
    } finally { setEnviando('') }
  }

  if (cargando) return <div style={st.wrap}><p style={{ color: colors.textSec }}>Cargando…</p></div>
  if (!data) return <div style={st.wrap}><div style={st.errorBox}>{error}</div></div>

  const { caso, pasos = [], entrega, cliente, reintegros = [], cobros = [] } = data
  const abierto = caso.estado === 'abierto'
  const repartoEditable = abierto && !reintegros.some(r => r.estado !== 'pendiente')
  const sumaReparto = Object.values(reparto).reduce((a, v) => a + (parseFloat(v) || 0), 0)
  const reintegroDe = (clave) => reintegros.find(r => `reintegro:${r.id}` === clave)

  const accionDelPaso = (p) => {
    if (!abierto || p.estado === 'hecho' || p.estado === 'bloqueado' || p.estado === 'en_curso') return null
    if (p.clave === 'retiro' && caso.medio_retiro === 'andreani') {
      return (
        <Boton variante="send" sm disabled={!!enviando}
          onClick={() => window.confirm('¿Pedir a Andreani que retire la mercadería en el domicilio del cliente?')
            && accion('retiro', () => casosApi.retiroAndreani(caso.id), 'Andreani tiene la orden de retiro.')}>
          {enviando === 'retiro' ? 'Pidiendo…' : '→ Pedir retiro a Andreani'}
        </Boton>
      )
    }
    if (p.clave === 'retiro') {
      return (
        <div style={st.accionFila}>
          <select style={st.input} value={form.fletero || ''} onChange={e => set('fletero', e.target.value)}>
            <option value="">Fletero…</option>
            {fleteros.map(f => <option key={f.id} value={f.id}>{f.nombre}</option>)}
          </select>
          <input type="date" style={st.input} value={form.fecha || hoyISO()} onChange={e => set('fecha', e.target.value)} />
          <Boton variante="primary" sm disabled={!!enviando || !form.fletero}
            onClick={() => accion('retiro', () => casosApi.retiroPropio(caso.id, { fletero_id: Number(form.fletero), fecha: form.fecha || hoyISO() }), 'Retiro programado.')}>
            Programar retiro
          </Boton>
        </div>
      )
    }
    if (p.clave === 'recepcion') {
      return (
        <div style={st.accionFila}>
          <input style={{ ...st.input, flex: 1 }} placeholder="Notas de la recepción (opcional)"
            value={form.notas || ''} onChange={e => set('notas', e.target.value)} />
          <Boton variante="success" sm disabled={!!enviando}
            onClick={() => accion('recepcion', () => casosApi.recibir(caso.id, form.notas), 'Mercadería recibida.')}>
            ✓ Recibida
          </Boton>
        </div>
      )
    }
    if (p.clave === 'devolucion') {
      return (
        <div style={st.accionFila}>
          <input style={{ ...st.input, ...sx.mono, flex: 1 }} placeholder="Nº de la DEVOLUCIÓN en Presea (varios, separados por coma)"
            value={form.numeros || ''} onChange={e => set('numeros', e.target.value)} />
          <Boton variante="send" sm disabled={!!enviando || !(form.numeros || '').trim()}
            onClick={() => accion('devolucion', () => casosApi.devolucionPresea(caso.id, form.numeros.trim()), 'DEVOLUCIÓN verificada en Presea.')}>
            {enviando === 'devolucion' ? 'Verificando…' : '→ Verificar en Presea'}
          </Boton>
        </div>
      )
    }
    if (p.clave === 'ajuste') {
      return (
        <div style={st.accionFila}>
          <input style={{ ...st.input, flex: 1 }} placeholder="Referencia del ajuste (opcional)"
            value={form.ajuste || ''} onChange={e => set('ajuste', e.target.value)} />
          <Boton variante="success" sm disabled={!!enviando}
            onClick={() => accion('ajuste', () => casosApi.ajusteInterno(caso.id, form.ajuste), 'Ajuste interno registrado.')}>
            ✓ Ajuste hecho
          </Boton>
        </div>
      )
    }
    const r = reintegroDe(p.clave)
    if (r) {
      const k = `r${r.id}`
      const esTransf = r.medio?.clave === 'transferencia'
      return (
        <div style={st.accionFila}>
          {esTransf && (
            <>
              <input style={st.input} placeholder="CBU o alias del cliente" value={form[`${k}cbu`] || ''} onChange={e => set(`${k}cbu`, e.target.value)} />
              <input style={st.input} placeholder="Titular" value={form[`${k}tit`] || ''} onChange={e => set(`${k}tit`, e.target.value)} />
            </>
          )}
          <input style={{ ...st.input, ...sx.mono, flex: 1, minWidth: 160 }} placeholder="Referencia del reintegro"
            value={form[`${k}ref`] || ''} onChange={e => set(`${k}ref`, e.target.value)} />
          <Boton variante="success" sm disabled={!!enviando || !(form[`${k}ref`] || '').trim()}
            onClick={() => accion(k, () => casosApi.reintegrar(caso.id, r.id, {
              referencia: form[`${k}ref`].trim(), cbu_alias: form[`${k}cbu`] || null, titular: form[`${k}tit`] || null,
            }), 'Reintegro registrado y contra-recibo enviado a Presea.')}>
            {enviando === k ? 'Registrando…' : '✓ Reintegrado'}
          </Boton>
        </div>
      )
    }
    return null
  }

  return (
    <div style={st.wrap}>
      <div style={sx.toolbar}>
        <Boton variante="ghost" sm onClick={() => navigate(-1)}>← Volver</Boton>
        <div style={st.breadcrumb}>
          <span>Casos</span><span style={{ color: colors.textPlh }}>›</span>
          <span style={st.breadcrumbNum}>#{caso.id}</span>
        </div>
        <span style={{ ...sx.badge, background: abierto ? colors.warnBg : colors.okBg, color: abierto ? colors.warn : colors.ok }}>
          {data.estado_caso?.etiqueta || (abierto ? 'Abierto' : 'Resuelto')}
        </span>
        <div style={{ flex: 1 }} />
        {caso.medio_retiro === 'propio' && caso.retiro_fletero_id && (
          <Boton variante="secondary" sm onClick={() => imprimirRemitoRetiro({ caso, entrega, cliente })}>🖨 Remito de retiro</Boton>
        )}
        {caso.retiro_andreani_agrupador && (
          <Boton variante="secondary" sm onClick={async () => {
            try { await abrirArchivoEnPestana(`/casos-entrega/${caso.id}/retiro-andreani/etiqueta`) } catch { setError('No se pudo abrir la etiqueta de Andreani.') }
          }}>🏷 Etiqueta Andreani</Boton>
        )}
      </div>

      {msg && <div style={st.successBox}>{msg}</div>}
      {error && <div style={st.errorBox}>{error}</div>}

      <div style={st.grid}>
        <div style={st.col}>
          <div style={sx.card}>
            <div style={st.docLabel}>{caso.tipo === 'fraude' ? 'Fraude detectado en tránsito' : 'Devolución por producto defectuoso'}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: space.lg, flexWrap: 'wrap', marginTop: space.xs }}>
              <div style={{ fontSize: text.title, fontWeight: 600 }}>{cliente?.nombre || entrega?.nombre}</div>
              <div style={{ ...sx.mono, fontSize: text.display, fontWeight: 600 }}>$ {fmtMonto(caso.importe)}</div>
            </div>
            <div style={{ display: 'flex', gap: space.lg, flexWrap: 'wrap', fontSize: text.sec, color: colors.textSec, marginTop: space.sm }}>
              {caso.nota_venta && (
                <Link style={st.link} to={`/notas-venta/${caso.nota_venta.id}`}>
                  Pedido <span style={sx.mono}>{caso.nota_venta.numero_lgr}</span>
                </Link>
              )}
              {caso.nota_venta?.numero_presea && <span>Factura <span style={sx.mono}>{caso.nota_venta.numero_presea}</span></span>}
              {caso.remito && (
                <Link style={st.link} to={`/logistica/remitos/${caso.remito.id}`}>
                  Remito <span style={sx.mono}>{caso.remito.numero_presea}</span>
                </Link>
              )}
              <span>Abierto por {caso.usuario?.nombre} · <span style={sx.mono}>{fmtFechaHora(caso.created_at)}</span></span>
            </div>
            <div style={{ marginTop: space.lg, fontSize: text.body, color: colors.textStrong }}>{caso.detalle}</div>
            <table style={{ ...sx.table, marginTop: space.lg }}>
              <thead><tr><th style={sx.th}>Código</th><th style={sx.th}>Producto</th><th style={{ ...sx.th, textAlign: 'right' }}>Devuelve</th></tr></thead>
              <tbody>
                {(caso.items || []).map(i => (
                  <tr key={i.articulo_id} style={st.tr}>
                    <td style={{ ...sx.td, ...sx.mono, color: colors.textSec }}>{i.codigo}</td>
                    <td style={sx.td}>{i.descripcion}</td>
                    <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{i.cantidad}</td>
                  </tr>
                ))}
                {caso.incluye_envio && (
                  <tr style={st.tr}><td style={{ ...sx.td, ...sx.mono, color: colors.textSec }}>ENV0000</td><td style={sx.td}>{caso.tipo === 'fraude' ? 'Envío' : 'Envío (se reintegra)'}</td><td style={sx.td} /></tr>
                )}
              </tbody>
            </table>
            <div style={{ marginTop: space.lg, fontSize: text.sec, color: colors.textSec }}>
              {caso.medio_retiro === 'transportista'
                ? <>La mercadería vuelve con el transportista. El cobro no se toca: el stock vuelve con la DEVOLUCIÓN en Presea.</>
                : caso.con_retiro
                ? <>Se retira la mercadería — {caso.medio_retiro === 'andreani' ? 'por Andreani (logística inversa)' : caso.medio_retiro === 'propio' ? 'con transporte propio' : 'el cliente la trae al local'}.</>
                : <>Sin retiro: la mercadería queda con el cliente (después de la DEVOLUCIÓN va un ajuste interno de stock).</>}
            </div>
          </div>

          <div style={sx.card}>
            <h3 style={sx.cardTitle}>Recorrido</h3>
            <div style={{ marginTop: space.md }}>
              {pasos.map(p => {
                const c = COLOR_PASO[p.estado] || COLOR_PASO.bloqueado
                return (
                  <div key={p.clave} style={st.paso}>
                    <span style={{ ...st.pasoIcono, color: c.color, background: c.bg }}>{ICONO[p.estado]}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: space.md, alignItems: 'baseline', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: p.estado === 'bloqueado' ? colors.textMeta : colors.text }}>{p.titulo}</span>
                        <span style={{ fontSize: text.meta, fontWeight: 600, color: colors.textMeta, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{p.quien}</span>
                      </div>
                      {p.detalle && <div style={{ fontSize: text.sec, color: colors.textSec, marginTop: space.xs }}>{p.detalle}</div>}
                      {p.clave === 'retiro' && caso.retiro_andreani_tracking && (
                        <a style={{ ...st.link, fontSize: text.sec }} href={`https://www.andreani.com/envio/${caso.retiro_andreani_tracking}`} target="_blank" rel="noreferrer">
                          Seguimiento en Andreani ↗
                        </a>
                      )}
                      {accionDelPaso(p) && <div style={{ marginTop: space.sm }}>{accionDelPaso(p)}</div>}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <div style={st.col}>
          {caso.tipo !== 'fraude' && (<div style={sx.card}>
            <h3 style={sx.cardTitle}>Reintegro por cobro</h3>
            <p style={{ fontSize: text.sec, color: colors.textSec, margin: `${space.sm}px 0 ${space.md}px` }}>
              La plata vuelve por el medio original de cada cobro. {repartoEditable ? 'El vendedor decide cuánto por cada uno.' : ''}
            </p>
            {repartoEditable ? (
              <>
                {cobros.map(c => (
                  <div key={c.id} style={st.cobro}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{c.medio}</div>
                      <div style={{ fontSize: text.sec, color: colors.textMeta }}>
                        Recibo <span style={sx.mono}>{c.numero_presea}</span> · disponible <span style={sx.mono}>$ {fmtMonto(c.disponible)}</span>
                      </div>
                    </div>
                    <input style={{ ...st.input, ...sx.mono, width: 120, textAlign: 'right' }} inputMode="decimal"
                      value={reparto[c.id] ?? ''} onChange={e => setReparto(prev => ({ ...prev, [c.id]: e.target.value }))} />
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: space.md, gap: space.md }}>
                  <span style={{ fontSize: text.sec, color: Math.abs(sumaReparto - caso.importe) > 0.009 ? colors.err : colors.textSec }}>
                    Suma <span style={sx.mono}>$ {fmtMonto(sumaReparto)}</span> de <span style={sx.mono}>$ {fmtMonto(caso.importe)}</span>
                  </span>
                  <Boton variante="secondary" sm disabled={!!enviando || Math.abs(sumaReparto - caso.importe) > 0.009}
                    onClick={() => accion('reparto', () => casosApi.repartir(caso.id, reparto), 'Reparto guardado.')}>
                    Guardar reparto
                  </Boton>
                </div>
              </>
            ) : (
              reintegros.map(r => (
                <div key={r.id} style={st.cobro}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{r.pago.medio}</div>
                    <div style={{ fontSize: text.sec, color: colors.textMeta }}>
                      {r.estado === 'hecho'
                        ? <>Ref. <span style={sx.mono}>{r.referencia}</span> · {r.hecho_por} · <span style={sx.mono}>{fmtFecha(r.hecho_at)}</span></>
                        : 'Pendiente'}
                    </div>
                  </div>
                  <span style={{ ...sx.mono, fontWeight: 600 }}>$ {fmtMonto(r.importe)}</span>
                </div>
              ))
            )}
          </div>)}

          {entrega && (
            <div style={sx.card}>
              <h3 style={sx.cardTitle}>Dirección de entrega</h3>
              <div style={{ marginTop: space.md, fontSize: text.body, lineHeight: 1.5 }}>
                {entrega.retira
                  ? <div style={{ color: colors.textSec }}>Retiro en sucursal: no hay dirección de entrega.</div>
                  : <div>{entrega.domicilio || <em style={{ color: colors.err }}>sin domicilio</em>}</div>}
                {!entrega.retira && <div>{entrega.localidad}{entrega.provincia ? `, ${entrega.provincia}` : ''} {entrega.codigo_postal && <>· CP <span style={sx.mono}>{entrega.codigo_postal}</span></>}</div>}
                {entrega.nombre && <div style={{ color: colors.textSec }}>Recibe {entrega.nombre}</div>}
                {entrega.telefono && <div style={{ color: colors.textSec }}>Tel. <span style={sx.mono}>{entrega.telefono}</span></div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const st = {
  wrap: { maxWidth: 1100, margin: '0 auto', paddingBottom: space['3xl'], fontFamily: font.sans, color: colors.text },
  breadcrumb: { display: 'flex', alignItems: 'center', gap: space.sm, fontSize: text.body, color: colors.textSec },
  breadcrumbNum: { ...sx.mono, color: colors.text, fontWeight: 500, background: colors.bgSoft, padding: '2px 8px', borderRadius: radius.xs },
  successBox: { background: colors.okBg, color: colors.ok, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.sm, marginBottom: space.lg, fontSize: text.body, fontWeight: 500 },
  errorBox: { background: colors.errBg, color: colors.err, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.sm, marginBottom: space.lg, fontSize: text.body },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: space.xl },
  col: { display: 'flex', flexDirection: 'column', gap: space.xl, minWidth: 0 },
  docLabel: { fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.08em' },
  link: { color: colors.link, cursor: 'pointer', textDecoration: 'none' },
  tr: { height: 44, borderBottom: `1px solid ${colors.borderSoft}` },
  paso: { display: 'flex', gap: space.lg, padding: `${space.lg}px 0`, borderBottom: `1px solid ${colors.borderSoft}` },
  pasoIcono: { width: 24, height: 24, borderRadius: radius.pill, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: text.sec, fontWeight: 700, flexShrink: 0 },
  accionFila: { display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap' },
  input: { ...sx.input, background: colors.bg },
  cobro: { display: 'flex', alignItems: 'center', gap: space.md, padding: `${space.md}px 0`, borderBottom: `1px solid ${colors.borderSoft}` },
}
